import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { CurrencyDollar, Plus, Tag } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PricingRulesPage() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ product_id: '', channel: 'default', base_price: 0, promotional_price: null, promo_start: '', promo_end: '', min_quantity_for_discount: 1, tiered_prices: [] });

  useEffect(() => { fetchRules(); }, []);

  const fetchRules = async () => {
    try {
      const { data } = await axios.get(`${API}/api/seller/pricing-rules`, { withCredentials: true });
      setRules(data.rules || []);
    } catch { toast.error('Failed to load pricing rules'); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.product_id) { toast.error('Product ID required'); return; }
    try {
      await axios.post(`${API}/api/seller/pricing-rules`, form, { withCredentials: true });
      toast.success('Pricing rule created');
      setDialogOpen(false);
      fetchRules();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
  };

  const addTier = () => setForm(f => ({ ...f, tiered_prices: [...f.tiered_prices, { min_qty: 10, price: 0 }] }));

  return (
    <DashboardLayout>
      <div className="p-6 max-w-5xl" data-testid="pricing-rules-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#0A0A0A]">Pricing Rules</h1>
            <p className="text-sm text-[#6b6b8a] mt-1">Channel-specific and tiered pricing configuration</p>
          </div>
          <Button data-testid="add-pricing-btn" onClick={() => { setForm({ product_id: '', channel: 'default', base_price: 0, promotional_price: null, promo_start: '', promo_end: '', min_quantity_for_discount: 1, tiered_prices: [] }); setDialogOpen(true); }} className="bg-[#7d64ff] hover:bg-[#6b4fef]">
            <Plus size={16} className="mr-2" /> Add Rule
          </Button>
        </div>

        {loading ? <div className="text-center py-12 text-[#9c9db7]">Loading...</div> : rules.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-[#e5e5ea]">
            <CurrencyDollar size={48} className="mx-auto text-[#9c9db7] mb-3" />
            <p className="text-[#6b6b8a]">No pricing rules configured.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-[#e5e5ea] overflow-hidden">
            <table className="w-full text-sm" data-testid="pricing-table">
              <thead className="bg-[#f9f9fb] border-b border-[#e5e5ea]">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-[#6b6b8a]">Product</th>
                  <th className="text-left px-4 py-3 font-medium text-[#6b6b8a]">Channel</th>
                  <th className="text-left px-4 py-3 font-medium text-[#6b6b8a]">Base Price</th>
                  <th className="text-left px-4 py-3 font-medium text-[#6b6b8a]">Promo</th>
                  <th className="text-left px-4 py-3 font-medium text-[#6b6b8a]">Tiers</th>
                </tr>
              </thead>
              <tbody>
                {rules.map(r => (
                  <tr key={r.rule_id} className="border-b border-[#f0f0f0] hover:bg-[#fafafe]">
                    <td className="px-4 py-3 font-mono text-xs">{r.product_id}</td>
                    <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full text-xs bg-[#7d64ff]/10 text-[#7d64ff] font-medium">{r.channel}</span></td>
                    <td className="px-4 py-3 font-semibold">&#8377;{r.base_price?.toLocaleString()}</td>
                    <td className="px-4 py-3">{r.promotional_price ? <span className="text-green-600 font-medium">&#8377;{r.promotional_price}</span> : <span className="text-[#9c9db7]">—</span>}</td>
                    <td className="px-4 py-3">{r.tiered_prices?.length || 0} tiers</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md" data-testid="pricing-dialog">
            <DialogHeader><DialogTitle>Add Pricing Rule</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Product ID</Label><Input data-testid="pricing-product-id" value={form.product_id} onChange={e => setForm(f => ({ ...f, product_id: e.target.value }))} placeholder="prod_..." /></div>
              <div><Label>Channel</Label><Input value={form.channel} onChange={e => setForm(f => ({ ...f, channel: e.target.value }))} placeholder="default, amazon, flipkart" /></div>
              <div><Label>Base Price</Label><Input type="number" value={form.base_price} onChange={e => setForm(f => ({ ...f, base_price: parseFloat(e.target.value) || 0 }))} /></div>
              <div><Label>Promo Price (optional)</Label><Input type="number" value={form.promotional_price || ''} onChange={e => setForm(f => ({ ...f, promotional_price: e.target.value ? parseFloat(e.target.value) : null }))} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Promo Start</Label><Input type="date" value={form.promo_start} onChange={e => setForm(f => ({ ...f, promo_start: e.target.value }))} /></div>
                <div><Label>Promo End</Label><Input type="date" value={form.promo_end} onChange={e => setForm(f => ({ ...f, promo_end: e.target.value }))} /></div>
              </div>
              <div>
                <div className="flex items-center justify-between"><Label>Tiered Prices</Label><Button variant="ghost" size="sm" onClick={addTier} className="text-[#7d64ff] text-xs">+ Add Tier</Button></div>
                {form.tiered_prices.map((t, i) => (
                  <div key={i} className="flex gap-2 mt-1">
                    <Input type="number" placeholder="Min Qty" value={t.min_qty} onChange={e => setForm(f => ({ ...f, tiered_prices: f.tiered_prices.map((tp, idx) => idx === i ? { ...tp, min_qty: parseInt(e.target.value) } : tp) }))} className="w-1/2" />
                    <Input type="number" placeholder="Price" value={t.price} onChange={e => setForm(f => ({ ...f, tiered_prices: f.tiered_prices.map((tp, idx) => idx === i ? { ...tp, price: parseFloat(e.target.value) } : tp) }))} className="w-1/2" />
                  </div>
                ))}
              </div>
              <Button data-testid="save-pricing-btn" onClick={handleCreate} className="w-full bg-[#7d64ff] hover:bg-[#6b4fef]">Create Rule</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
